import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { type AdminUser, listAdminUsers } from '../api/admin'
import { addProjectMember, listProjectMembers, removeProjectMember } from '../api/project-members'
import type { ProjectMember, ProjectRole, UserRole } from '../types/api'
import { canManageProject, isGlobalAdmin } from '../utils/rbac'

interface ProjectMembersPanelProps {
  projectId: number
  projectRole: ProjectRole | undefined
  globalRole: string | undefined
}

export default function ProjectMembersPanel({
  projectId,
  projectRole,
  globalRole,
}: ProjectMembersPanelProps) {
  const [members, setMembers] = useState<ProjectMember[]>([])
  const [allUsers, setAllUsers] = useState<AdminUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAdding, setIsAdding] = useState(false)
  const [removingUserId, setRemovingUserId] = useState<number | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('')
  const [newMemberRole, setNewMemberRole] = useState<'manager' | 'viewer'>('viewer')
  const [manualUserId, setManualUserId] = useState('')

  const canManage = canManageProject(projectRole)
  const globalAdmin = isGlobalAdmin(globalRole as UserRole | undefined)

  const loadMembers = useCallback(async () => {
    try {
      const data = await listProjectMembers(projectId)
      setMembers(data)
    } catch {
      toast.error('Failed to load members')
    } finally {
      setIsLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    void loadMembers()
  }, [loadMembers])

  useEffect(() => {
    if (!canManage || !globalAdmin) return

    void listAdminUsers()
      .then(setAllUsers)
      .catch(() => setAllUsers([]))
  }, [canManage, globalAdmin])

  const memberUserIds = new Set(members.map((m) => m.userId))
  const availableUsers = allUsers.filter((u) => u.isActive && !memberUserIds.has(u.id))

  const canRemoveMember = (member: ProjectMember): boolean => {
    if (!canManage) return false
    if (globalAdmin) return true
    return projectRole === 'manager' && member.projectRole === 'viewer'
  }

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    const userId = globalAdmin ? Number(selectedUserId) : Number(manualUserId)
    if (!Number.isFinite(userId) || userId <= 0) {
      toast.error('Select or enter a valid user')
      return
    }

    const role = globalAdmin ? newMemberRole : 'viewer'
    if (role === 'manager' && !globalAdmin) {
      toast.error('Only admins can assign managers')
      return
    }

    setIsAdding(true)
    try {
      await addProjectMember(projectId, { userId, projectRole: role })
      toast.success('Member added')
      setSelectedUserId('')
      setManualUserId('')
      setNewMemberRole('viewer')
      await loadMembers()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add member')
    } finally {
      setIsAdding(false)
    }
  }

  const handleRemove = async (userId: number) => {
    setRemovingUserId(userId)
    try {
      await removeProjectMember(projectId, userId)
      toast.success('Member removed')
      await loadMembers()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove member')
    } finally {
      setRemovingUserId(null)
    }
  }

  return (
    <div className="bg-harbor-surface border border-harbor-border rounded-xl">
      <div className="p-6 border-b border-harbor-border">
        <h2 className="text-lg font-semibold text-harbor-text">Members</h2>
        <p className="text-sm text-harbor-text-secondary mt-1">
          {canManage
            ? globalAdmin
              ? 'Assign managers or viewers to this project.'
              : 'Assign viewers to this project.'
            : 'People with access to this project.'}
        </p>
      </div>

      {isLoading ? (
        <div className="p-8 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500" />
        </div>
      ) : members.length === 0 ? (
        <div className="p-8 text-center text-sm text-harbor-text-secondary">No members yet.</div>
      ) : (
        <div className="divide-y divide-harbor-border">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-harbor-surface-raised/30"
            >
              <div className="min-w-0">
                <p className="font-medium text-harbor-text truncate">{member.username}</p>
                <p className="text-sm text-harbor-text-secondary truncate">{member.email}</p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-xs px-2 py-1 rounded-md bg-harbor-surface-raised text-harbor-text-secondary capitalize">
                  {member.projectRole}
                </span>
                {canRemoveMember(member) && (
                  <button
                    type="button"
                    onClick={() => void handleRemove(member.userId)}
                    disabled={removingUserId === member.userId}
                    className="text-sm text-red-400 hover:text-red-300 disabled:opacity-50"
                  >
                    {removingUserId === member.userId ? 'Removing…' : 'Remove'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <form onSubmit={handleAdd} className="p-6 border-t border-harbor-border space-y-4">
          {globalAdmin ? (
            <>
              <div>
                <label
                  htmlFor="member-user"
                  className="block text-sm font-medium text-harbor-text-secondary mb-1"
                >
                  User
                </label>
                <select
                  id="member-user"
                  value={selectedUserId}
                  onChange={(e) =>
                    setSelectedUserId(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  required
                  className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select a user…</option>
                  {availableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.username} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="member-role"
                  className="block text-sm font-medium text-harbor-text-secondary mb-1"
                >
                  Project role
                </label>
                <select
                  id="member-role"
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value as 'manager' | 'viewer')}
                  className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="viewer">Viewer</option>
                  <option value="manager">Manager</option>
                </select>
              </div>
            </>
          ) : (
            <div>
              <label
                htmlFor="member-user-id"
                className="block text-sm font-medium text-harbor-text-secondary mb-1"
              >
                User ID
              </label>
              <input
                id="member-user-id"
                type="number"
                min={1}
                value={manualUserId}
                onChange={(e) => setManualUserId(e.target.value)}
                required
                className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="User ID from your administrator"
              />
              <p className="mt-1 text-xs text-harbor-text-muted">Managers can add viewers only.</p>
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isAdding}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              {isAdding ? 'Adding…' : 'Add member'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
